import Image from 'next/image';
import { Trans, useTranslation } from 'next-i18next';

import Button from '@/common/button';
import { ComparisonStats } from '@/common/comparison-stats';
import Header from '@/common/header';
import { LinksMirrorDownload } from '@/common/link-mirror-download';
import Sponsors from '@/components/sponsors';
import { useTrackSoftwareDownload } from '@/hooks/use-track-software-download';

import { Footer } from '../footer';
import { SignUpCustomTrial } from '../sign-up-custom-trial';
import type { DefaultTrialProps } from '../trial-layout-switcher';

interface ProofOnScreenProps extends DefaultTrialProps {}

export function ProofOnScreen({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: ProofOnScreenProps) {
  const { t } = useTranslation('components/trial/lp/proof-on-screen');
  const urlDownload = LinksMirrorDownload();
  const trackDownload = useTrackSoftwareDownload('trial_proof_on_screen');

  function handleDownloadClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    // Tracking must not hold the installer navigation.
    setTimeout(() => {
      window.location.href = urlDownload;
    }, 300);
    trackDownload(urlDownload);

    fetch('/api/ga4-download', {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        events: [
          {
            name: 'download_click',
            params: {
              idioma_navegador: navigator?.language || 'NP_UNKNOWN_NP',
              pais: 'NP_UNKNOWN_NP', // será sobrescrito no backend
              origem: sessionStorage.getItem('idref') || 'NP_UNKNOWN_NP',
              ip_do_usuario_no_site: 'NP_UNKNOWN_NP', // será sobrescrito no backend
            },
          },
        ],
      }),
    }).catch(() => {});
  }

  return (
    <>
      <Header variant='primary' />
      <section className="mx-auto w-full bg-[url('/assets/images/trial/fundo_kohogs.webp')] bg-cover bg-center bg-no-repeat">
        <div className='mx-auto mt-10 flex flex-col items-start justify-center gap-4 px-2.5 ipad:flex-row'>
          <SignUpCustomTrial daysTrial={daysTrial} trialConfigId={trialConfigId} trialPageId={trialPageId} showLogo isDisabledButton={isDisabledButton} />
          <div className='flex w-full flex-col'>
            <h1 className='text-6xl font-bold uppercase text-white'>{t('first_section.title')}</h1>
            <h2 className='text-10xl font-bold text-[#93FF18]'>{t('first_section.subtitle')}</h2>
            <p className='text-4xl font-medium text-white'>{t('first_section.description')}</p>
            <Image alt='fire jet' src='/assets/images/boost/image.webp' width={656} height={552} />
          </div>
        </div>
      </section>

      <Sponsors />
      <section className='mx-auto mt-20 max-w-[1400px] px-2.5'>
        <h3 className='mb-3 text-center text-4xl font-bold uppercase leading-7 text-white tablet:text-7xl tablet:leading-[100%]'>
          <Trans i18nKey='servers.title' components={{ span: <span className='text-[#93FF18]' /> }}>
            {t('servers.title')}
          </Trans>
        </h3>
        <Image src='/assets/images/trial/new/servers-map-black.webp' alt='Mapa de servidores NoPing' width={1204} height={719} className='w-full max-w-[1204px] object-cover' />
      </section>

      <section className='mx-auto mb-10 flex w-full max-w-6xl flex-col px-2.5'>
        <h3 className='mt-10 px-2.5 text-center text-4xl font-bold text-white tablet:text-7xl tablet:leading-12'>
          <Trans i18nKey='second_section.text01' components={{ span: <span className='text-[#93FF18]' /> }}>
            {t('second_section.text01')}
          </Trans>
        </h3>
        <div className='mx-auto w-full items-center justify-center px-2.5 py-20'>
          <div className='flex flex-col items-center justify-center gap-14 tablet:flex-row'>
            <div className='flex flex-col gap-5'>
              <p className='text-center text-3xl font-medium uppercase text-white tablet:text-5xl'>
                <Trans i18nKey='second_section.text02' components={{ span: <span className='font-bold text-[#EB0013]' /> }}>
                  {t('second_section.text02')}
                </Trans>
              </p>
              <div className='relative'>
                <video autoPlay muted loop aria-hidden='true' className='w-full max-w-[450px] rounded-xl border-8 border-[#EB0013]'>
                  <source src='/assets/video/trial/video-lagado.mp4' type='video/mp4' />
                </video>
                <div className='absolute -bottom-5 start-4 max-h-[70px] max-w-[200px] rounded-ee-[15px] rounded-ss-[15px] bg-[#EB0013] px-2.5 py-1 text-center leading-7 text-white'>
                  <p className='text-lg font-bold sm:text-2xl'>{t('second_section.text03')}</p>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-5'>
              <p className='text-center text-3xl font-medium uppercase text-white tablet:text-5xl'>
                <Trans i18nKey='second_section.text04' components={{ span: <span className='font-bold text-[#47FF18] underline' /> }}>
                  {t('second_section.text04')}
                </Trans>
              </p>
              <div className='relative'>
                <video autoPlay muted loop aria-hidden='true' className='w-full max-w-[450px] rounded-xl border-8 border-[#47FF18]' style={{ boxShadow: '0px 0px 40px 0px #93FF1840' }}>
                  <source src='/assets/video/trial/video-deslagado.mp4' type='video/mp4' />
                </video>
                <div className='absolute -bottom-5 start-4 max-h-[70px] max-w-[200px] rounded-ee-[15px] rounded-ss-[15px] bg-[#47FF18] px-2.5 py-1 text-center leading-7 text-black'>
                  <p className='text-lg font-bold sm:text-2xl'>{t('second_section.text05')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Button variant='primary' fullWidth disabled={isDisabledButton}>
          <span>{t('cta_button', { count: daysTrial })}</span>
        </Button>
      </section>

      <ComparisonStats
        title={t('boost_fps.sixth_section.title')}
        description={t('boost_fps.sixth_section.content')}
        withNoPingLabel={t('boost_fps.sixth_section.with_noping')}
        withoutNoPingLabel={t('boost_fps.sixth_section.without_noping')}
        showBackground={false}
        items={[
          {
            percentage: '60%',
            title: t('boost_fps.sixth_section.items.first_item.title'),
            description: t('boost_fps.sixth_section.items.first_item.content'),
            withoutNoPing: { value: '80 FPS', height: '174px' },
            withNoPing: { value: '128 FPS', height: '252px' },
          },
          {
            percentage: '35%',
            title: t('boost_fps.sixth_section.items.second_item.title'),
            description: t('boost_fps.sixth_section.items.second_item.content'),
            withoutNoPing: { value: t('boost_fps.sixth_section.items.second_item.without_noping_usage_cpu'), height: '252px' },
            withNoPing: { value: t('boost_fps.sixth_section.items.second_item.with_noping_usage_cpu'), height: '174px' },
          },
          {
            percentage: '99%',
            title: t('boost_fps.sixth_section.items.third_item.title'),
            description: t('boost_fps.sixth_section.items.third_item.content'),
            withoutNoPing: { value: '120 MS', height: '252px' },
            withNoPing: { value: '10 MS', height: '174px' },
          },
        ]}
      />

      <section className='mx-auto mt-20 max-w-[1400px] px-2.5'>
        <h3 className='text-center text-6xl font-bold text-white'>{t('download_section.title')}</h3>

        <div className='mt-10 flex w-full flex-col items-center justify-center gap-4 tablet:flex-row'>
          <a href={urlDownload} onClick={handleDownloadClick} download className='transition-all duration-300 hover:shadow-green-glow'>
            <Image src={t('img-download')} alt='Download image' width={455} height={509} />
          </a>

          <div className='h-0.5 w-full bg-[#FFFFFF33] tablet:h-auto tablet:w-0.5 tablet:self-stretch' />
          <div className='flex flex-col gap-5'>
            <div className='flex flex-col gap-2.5'>
              <p className='font-bold uppercase text-white'>{t('boost_fps.fifth_section.system_requirements.title')}</p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.system_requirements.content.os' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.system_requirements.content.os')}
                </Trans>
              </p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.system_requirements.content.memory' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.system_requirements.content.memory')}
                </Trans>
              </p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.system_requirements.content.ramstorage' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.system_requirements.content.ramstorage')}
                </Trans>
              </p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.system_requirements.content.mbinternet' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.system_requirements.content.mbinternet')}
                </Trans>
              </p>
            </div>
            <div className='flex flex-col gap-2.5'>
              <p className='font-bold uppercase text-white'>{t('boost_fps.fifth_section.recommended.title')}</p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.recommended.content.os' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.recommended.content.os')}
                </Trans>
              </p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.recommended.content.memory' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.recommended.content.memory')}
                </Trans>
              </p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.recommended.content.ramstorage' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.recommended.content.ramstorage')}
                </Trans>
              </p>
              <p className='text-sm font-bold uppercase text-[#B3B3BE]'>
                <Trans i18nKey='boost_fps.fifth_section.recommended.content.mbinternet' ns='components/trial/lp/proof-on-screen' components={{ br: <br /> }}>
                  {t('boost_fps.fifth_section.recommended.content.mbinternet')}
                </Trans>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer variant='minimal' text={t('footer_text', { year: new Date().getFullYear() })} />
    </>
  );
}
